import { type Graphics, Polygon, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo } from "react";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type {
    LayerId,
    MapItemHandler,
    Point2D,
    WarningMapObject,
} from "../types";

const NORMAL_HALF_SIZE_WORLD = 0.5;
const STROKE_WIDTH_WORLD = 0.1;
const ACTIVE_OUTLINE_WIDTH_WORLD = 0.05;
const CULL_HALF_SIZE_WORLD = NORMAL_HALF_SIZE_WORLD + STROKE_WIDTH_WORLD / 2;
const WARNING_COLOR = 0xfff000;
const DETAIL_COLOR = 0x333333;

interface WarningObjectProps {
    item: WarningMapObject;
    layerId: LayerId;
    selected: boolean;
    onItemClick?: MapItemHandler<WarningMapObject>;
    onItemHover?: MapItemHandler<WarningMapObject>;
    onItemLeave?: MapItemHandler<WarningMapObject>;
}

type Triangle = readonly [Point2D, Point2D, Point2D];

function getTriangleWorld(item: WarningMapObject, halfSize: number): Triangle {
    return [
        { x: item.x - halfSize, y: item.y - halfSize },
        { x: item.x, y: item.y + halfSize },
        { x: item.x + halfSize, y: item.y - halfSize },
    ];
}

function useWarningGeometry(item: WarningMapObject) {
    const { coordinates } = useScene();

    return useMemo(() => {
        const [baseLeftWorld, tipWorld, baseRightWorld] = getTriangleWorld(
            item,
            NORMAL_HALF_SIZE_WORLD,
        );
        const baseLeft = coordinates.worldToScene(baseLeftWorld);
        const tip = coordinates.worldToScene(tipWorld);
        const baseRight = coordinates.worldToScene(baseRightWorld);
        const cullSceneRect = coordinates.worldRectToScene({
            x: item.x - CULL_HALF_SIZE_WORLD,
            y: item.y - CULL_HALF_SIZE_WORLD,
            width: CULL_HALF_SIZE_WORLD * 2,
            height: CULL_HALF_SIZE_WORLD * 2,
        });

        return {
            triangle: [baseLeft, tip, baseRight] as Triangle,
            hitArea: new Polygon([
                baseLeft.x,
                baseLeft.y,
                tip.x,
                tip.y,
                baseRight.x,
                baseRight.y,
            ]),
            cullArea: new Rectangle(
                cullSceneRect.x,
                cullSceneRect.y,
                cullSceneRect.width,
                cullSceneRect.height,
            ),
            exclamationTop: coordinates.worldToScene({
                x: item.x,
                y: item.y + 0.15,
            }),
            exclamationStemBottom: coordinates.worldToScene({
                x: item.x,
                y: item.y - 0.25,
            }),
            exclamationDotTop: coordinates.worldToScene({
                x: item.x,
                y: item.y - 0.3,
            }),
            exclamationDotBottom: coordinates.worldToScene({
                x: item.x,
                y: item.y - 0.4,
            }),
            strokeWidth: coordinates.worldLengthToScene(STROKE_WIDTH_WORLD),
            activeOutlineWidth: coordinates.worldLengthToScene(
                ACTIVE_OUTLINE_WIDTH_WORLD,
            ),
        };
    }, [coordinates, item]);
}

function drawTrianglePath(graphics: Graphics, triangle: Triangle) {
    const [baseLeft, tip, baseRight] = triangle;
    graphics.poly([
        baseLeft.x,
        baseLeft.y,
        tip.x,
        tip.y,
        baseRight.x,
        baseRight.y,
    ]);
}

const WarningObject = ({
    item,
    layerId,
    selected,
    onItemClick,
    onItemHover,
    onItemLeave,
}: WarningObjectProps) => {
    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });
    const active = interaction.hovered || selected;
    const {
        triangle,
        hitArea,
        cullArea,
        exclamationTop,
        exclamationStemBottom,
        exclamationDotTop,
        exclamationDotBottom,
        strokeWidth,
        activeOutlineWidth,
    } = useWarningGeometry(item);
    const draw = useCallback(
        (graphics: Graphics) => {
            graphics.clear();
            drawTrianglePath(graphics, triangle);
            graphics.fill({ color: WARNING_COLOR });
            graphics.stroke({ color: DETAIL_COLOR, width: strokeWidth });

            if (active) {
                drawTrianglePath(graphics, triangle);
                graphics.stroke({
                    color: ACTIVE_INTERACTION_COLOR,
                    width: activeOutlineWidth,
                    alignment: -1,
                });
            }

            graphics.moveTo(exclamationTop.x, exclamationTop.y);
            graphics.lineTo(exclamationStemBottom.x, exclamationStemBottom.y);
            graphics.moveTo(exclamationDotTop.x, exclamationDotTop.y);
            graphics.lineTo(exclamationDotBottom.x, exclamationDotBottom.y);
            graphics.stroke({ color: DETAIL_COLOR, width: strokeWidth });
        },
        [
            exclamationDotBottom,
            exclamationDotTop,
            exclamationStemBottom,
            exclamationTop,
            active,
            activeOutlineWidth,
            strokeWidth,
            triangle,
        ],
    );

    return (
        <pixiContainer
            label="warning-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={cullArea}
        >
            <pixiGraphics
                eventMode={interaction.eventMode}
                cursor={interaction.cursor}
                hitArea={hitArea}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={draw}
            />
        </pixiContainer>
    );
};

export default memo(WarningObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.selected === next.selected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
